/**
 * PlanRolesAdmin.tsx — Admin > Plans > Plan Roles.
 *
 * Legacy's Plan Roles page (admin_role_details.php) for the signed-in account:
 * the names the account uses for the four built-in staff roles, in legacy's
 * order and labels — Primary Provider (Doctor), Secondary Provider (Nurse),
 * Facilitator (Coordinator) and Initial Contact (Receptionist). Each title is
 * required, up to 45 characters.
 * Save sits top right, as on the other settings pages, with Unsaved changes
 * and Revert beside it; changed fields carry the "modified" pill. Saving
 * refreshes the role titles everywhere Prospera shows them (scheduler,
 * resources, tasks, Account Details).
 * On a Locked page (useAdminPageLevel) the titles are read-only values, with
 * no Save or Revert and a subtitle that does not ask for a rename.
 */
import { useEffect, useState } from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { AdminLockedFieldset } from '@/components/admin/AdminLockedFieldset';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { DialogField, fieldClasses } from '@/components/admin/DialogChrome';
import { useTrackedForm } from '@/components/admin/useTrackedForm';
import { getErrorMessage } from '@/services/apiClient';
import { ROLE_FIELDS, ROLE_TITLE_MAX, getRoleTitles, saveRoleTitles, type RoleTitles } from '@/services/roleTitlesAdmin.service';
import { toast } from '@/store/toastStore';

const EMPTY: RoleTitles = { Doctor: '', Nurse: '', Coordinator: '', Receptionist: '' };

export function PlanRolesAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const t = useTrackedForm<RoleTitles>(() => EMPTY);
  const { reset } = t;

  useEffect(() => {
    let cancelled = false;
    getRoleTitles()
      .then((titles) => {
        if (!cancelled) reset(titles);
      })
      .catch((err) => {
        if (cancelled) return;
        setFailed(true);
        toast.error(getErrorMessage(err, 'Failed to load the role titles'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reset]);

  const missing = ROLE_FIELDS.filter(({ role }) => t.form[role].trim() === '');

  async function save() {
    if (missing.length > 0) return;
    setSaving(true);
    try {
      const saved = await saveRoleTitles(t.form);
      t.reset(saved);
      // Every screen that names a role reads these.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['roles'] }),
        queryClient.invalidateQueries({ queryKey: ['admin-account-summary'] }),
      ]);
      toast.success('Role titles saved');
    } catch (err) {
      toast.error(getErrorMessage(err, 'The role titles could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  const actions = (
    <div className="flex flex-wrap items-center gap-3">
      {t.dirty && (
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 text-sm text-blue-700">
            <AlertTriangle className="h-4 w-4" aria-hidden />
            Unsaved changes
          </span>
          <Button
            variant="ghost"
            size="xs"
            className="h-7 px-2 text-xs text-slate-500 hover:text-slate-700"
            leftIcon={<RotateCcw className="h-3.5 w-3.5" />}
            aria-label="Revert all changes"
            onClick={t.revertAll}
          >
            Revert
          </Button>
        </div>
      )}
      <Button onClick={() => void save()} disabled={!t.dirty || missing.length > 0 || loading || failed} loading={saving}>
        Save
      </Button>
    </div>
  );

  return (
    <div className="flex h-full flex-col overflow-hidden p-6">
      <div className="flex-shrink-0">
        <PageHeader
          title="Plan Roles"
          subtitle={locked ? "The practice's names for the four staff roles" : 'Name the four staff roles the way your practice does'}
          actions={locked ? undefined : actions}
        />
      </div>
      {loading ? (
        <SpinnerOverlay label="Loading role titles…" />
      ) : failed ? (
        <p className="py-16 text-center text-sm text-slate-500">The role titles could not be loaded. Reload the page to try again.</p>
      ) : (
        <AdminLockedFieldset>
          <div className="min-h-0 flex-1 overflow-y-auto pb-6">
            <section className="max-w-xl space-y-4 rounded-lg border border-slate-200 bg-white p-5">
              {ROLE_FIELDS.map(({ role, label }) => {
                const blank = t.form[role].trim() === '';
                const id = `plan-role-${role.toLowerCase()}`;
                return (
                  <DialogField
                    key={role}
                    label={label}
                    htmlFor={id}
                    required
                    state={blank ? 'error' : 'none'}
                    labelHint={blank ? '— required' : undefined}
                    badge={t.badge(role, label)}
                    description={`The ${role} role. Shown wherever Prospera names it.`}
                  >
                    <Input
                      id={id}
                      value={t.form[role]}
                      maxLength={ROLE_TITLE_MAX}
                      placeholder={role}
                      className={fieldClasses.input(blank ? 'error' : 'none')}
                      onChange={(e) => t.patch({ [role]: e.target.value } as Partial<RoleTitles>)}
                    />
                  </DialogField>
                );
              })}
            </section>
          </div>
        </AdminLockedFieldset>
      )}
    </div>
  );
}
